import { useContext } from 'react';
import {
    ActivityInput,
    ManagedLinkedPeopleInput,
    ProductionInput,
    ProductionOrganizationInfoInput,
    Reference,
    ResourceInput
} from 'sr-types/lib/production/v1/graphql';
import { UserContext } from '../../common/auth/UserContext';
import { CONSTANTS_ID } from '../../common/constants/constants';
import { EMPTY_SCHEDULE } from '../../common/form/widgets/ScheduleWidget';
import { useMutationWithContext } from '../../common/hooks/useMutationWithContext';
import { useSaveNote } from '../../common/notes';
import { deriveNoteTitleFromContent } from '../../common/notes/utils';
import { failedToastMessage } from '../../common/utils/commonUtils';
import useHistory from '../../common/utils/useHistory';
import { QuickAddOrganizationInput } from '../../organization/QuickAddOrganization';
import { PERSON_EMPTY_LAST_NAME } from '../../person/person';
import { toInputForSaveResource } from '../../resource/resourceUtils';
import {
    ProposalFormType,
    ReferencePersonObjectType
} from '../form/productionTemplate/CreateProposalInfoFromTemplateForm';
import { EMPTY_ACTIVITY, handleActivitySaveInput } from '../helpers/activityUtils';
import { client as ProductionClient, saveQuery as ProductionSaveQuery } from '../helpers/production';
import {
    useBulkActivitySave,
    useBulkAssociatePeople,
    useSaveKeyPeople,
    useSaveProductionResources
} from '../helpers/productionApi';

export const useCreateProductionResourceShortlist = () => {
    const [saveProduction, { loading: isSavingProduction }] = useMutationWithContext(
        ProductionSaveQuery,
        ProductionClient
    );

    const saveProductionFromStepperTemplate = (prodData: ProductionInput) => {
        const id = prodData.identity?.id || '';
        return new Promise((resolve, reject) => {
            saveProduction({
                variables: {
                    input: {
                        identity: {
                            id: prodData.identity?.id || ''
                        },
                        ...prodData
                    }
                }
            })
                .then((res) => {
                    if (res?.data?.saveProduction?.errors) {
                        failedToastMessage('form.save.failure', { name: prodData.publicTitle });
                        reject(res.data.saveProduction.errors);
                    } else if (res.data?.saveProduction?.id) {
                        resolve({ id: res.data.saveProduction.id, reference: res.data.saveProduction.reference });
                    }
                })
                .catch((err) => {
                    failedToastMessage('form.save.failure', { name: prodData.publicTitle });
                    reject(err);
                });
        });
    };

    return { saveProductionFromStepperTemplate, isSavingProduction };
};

export const useProposalInfoStepSaveHandling = () => {
    const { saveKeyPeople } = useSaveKeyPeople();
    const { saveNewNote } = useSaveNote();
    const { bulkAssociatePeople } = useBulkAssociatePeople();

    const getProposalInfoFromStepperTemplateMutations = (
        newProdId: string,
        newProdRef: Reference,
        proposalData: ProposalFormType
    ) => {
        const submittedBy = transformToManagedLinkedPeopleInput(proposalData.submittedBy);
        const submittedTo = transformToManagedLinkedPeopleInput(proposalData.submittedTo);

        const proposalInfoPromises = [
            bulkAssociatePeople(newProdId, [...submittedBy, ...submittedTo]),
            saveKeyPeople(newProdId, [
                ...transformManagedLinkedPeopleInputToContactInput(submittedBy),
                ...transformManagedLinkedPeopleInputToContactInput(submittedTo)
            ])
        ];
        proposalData.notes &&
            proposalInfoPromises.push(
                saveNewNote({
                    input: {
                        content: proposalData.notes,
                        title: deriveNoteTitleFromContent(proposalData.notes),
                        entityReference: { ...newProdRef },
                        identity: {
                            id: ''
                        }
                    }
                })
            );
        return proposalInfoPromises;
    };

    return { getProposalInfoFromStepperTemplateMutations };
};

export const transformToManagedLinkedPeopleInput = (
    peopleList: ReferencePersonObjectType[]
): ManagedLinkedPeopleInput[] => {
    return peopleList.map((person) => {
        const { ref, name, ...rest } = person;
        const nameArray = name.split(' ');
        const nameInput = {
            firstName: nameArray[0],
            lastName: nameArray.length > 1 ? nameArray[nameArray.length - 1] : PERSON_EMPTY_LAST_NAME
        };
        return {
            new: {
                contact: { personReference: ref, name: nameInput, ...rest },
                isHired: false
            }
        };
    });
};

const transformManagedLinkedPeopleInputToContactInput = (peopleList: ManagedLinkedPeopleInput[]) => {
    return peopleList.map((person) => {
        const contact = person.new?.contact;
        return {
            personReference: contact?.personReference,
            name: contact?.name,
            role: contact?.role,
            productionRole: contact?.productionRole
        };
    });
};

export const useCreateResourceShortlistActivityForProduction = () => {
    const { saveResourcesToProduction } = useSaveProductionResources();
    const { bulkActivitySave } = useBulkActivitySave();

    const getResourcesActivityFromStepperTemplateMutations = (
        newProdId: string,
        resData: [ResourceInput],
        actData: [ActivityInput],
        proposalInfoSubmissionDate: string
    ) => {
        const bulkResourcesInput = resData.map(
            (res) => toInputForSaveResource(CONSTANTS_ID.createId, res) as ResourceInput
        );

        let activityInput = [];
        if (proposalInfoSubmissionDate) {
            const milestoneActData: ActivityInput = {
                ...EMPTY_ACTIVITY,
                dateRange: {
                    ...EMPTY_SCHEDULE,
                    durationSpecified: false,
                    start: proposalInfoSubmissionDate
                },
                name: 'Submission Date',
                activityType: 'Milestone'
            };
            activityInput.push(handleActivitySaveInput(milestoneActData));
        }

        const resourceShortlistActivityPromises = [
            saveResourcesToProduction({ input: bulkResourcesInput, productionId: newProdId, createShortlist: false }),
            bulkActivitySave(newProdId, '', activityInput, false)
        ];

        return resourceShortlistActivityPromises;
    };

    return { getResourcesActivityFromStepperTemplateMutations };
};

export const useHandleAssociateToProductionMutations = () => {
    const { application } = useContext(UserContext);
    const { changeRoute } = useHistory();
    const { getProposalInfoFromStepperTemplateMutations } = useProposalInfoStepSaveHandling();
    const { getResourcesActivityFromStepperTemplateMutations } = useCreateResourceShortlistActivityForProduction();

    const associateEntitiesWithProduction = async ({
        productionId,
        productionRef,
        proposalData,
        resData,
        activityData
    }) => {
        try {
            const res = await Promise.allSettled([
                ...getProposalInfoFromStepperTemplateMutations(productionId, productionRef, proposalData),
                ...getResourcesActivityFromStepperTemplateMutations(
                    productionId,
                    resData,
                    activityData,
                    proposalData.submissionDate
                )
            ]);
            return res;
        } finally {
            return changeRoute(`${application.home}/workspace/${productionId}/details`, {
                newProduction: 'true'
            });
        }
    };

    return { associateEntitiesWithProduction };
};

export const getAssociatedOrganization = (
    organizations: QuickAddOrganizationInput[]
): ProductionOrganizationInfoInput[] => {
    return organizations?.map((org) => ({
        name: org.label,
        organizationReference: org.ref,
        organizationTypes: org.organizationTypes
    }));
};
